import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import api, { formatApiError, API } from "@/lib/api";
import {
  HeartHandshake, Loader2, AlertTriangle, Upload, FileText,
  CheckCircle2, User, Calendar, DollarSign, ExternalLink, ScrollText,
} from "lucide-react";

/* ═══════════════════════════════════════════════════════════════
   Mis Mediaciones · /app/mis-mediaciones
   Hub personal: mediador ve sus casos + estado pago + subir acta.
   Solicitante ve sus solicitudes + estado pago + acceso al detalle.
   ═══════════════════════════════════════════════════════════════ */

const STATUS_META = {
  // ── 15 estados canónicos del backend (server.py :: MEDIATION_STATUS) ──
  solicitud_enviada:                      { label: "Solicitud enviada",                    cls: "border-slate-400/40 bg-slate-400/10 text-slate-300" },
  solicitud_aceptada:                     { label: "Solicitud aceptada",                   cls: "border-cyan-400/40 bg-cyan-400/10 text-cyan-200" },
  pago_inicial_pendiente:                 { label: "Pago inicial pendiente",               cls: "border-amber-400/40 bg-amber-400/10 text-amber-300" },
  pago_inicial_confirmado:                { label: "Pago inicial confirmado",              cls: "border-emerald-400/40 bg-emerald-400/10 text-emerald-300" },
  mediacion_en_curso:                     { label: "Mediación en curso",                   cls: "border-cyan-400/40 bg-cyan-400/10 text-cyan-200" },
  sesion_adicional_pendiente_pago:        { label: "Sesión adicional · pendiente pago",    cls: "border-amber-400/40 bg-amber-400/10 text-amber-300" },
  sesion_adicional_confirmada:            { label: "Sesión adicional confirmada",          cls: "border-emerald-400/40 bg-emerald-400/10 text-emerald-300" },
  cierre_sin_acuerdo_pendiente_documento: { label: "Pendiente: subir Acta Frustrada",      cls: "border-orange-400/40 bg-orange-400/10 text-orange-300" },
  cierre_con_acuerdo_pendiente_pago:      { label: "Cierre con acuerdo · pendiente pago",  cls: "border-amber-400/40 bg-amber-400/10 text-amber-300" },
  pago_cierre_confirmado:                 { label: "Pendiente: subir Acta con Acuerdo",    cls: "border-orange-400/40 bg-orange-400/10 text-orange-300" },
  documento_final_pendiente_aprobacion:   { label: "Acta enviada · esperando aprobación",  cls: "border-cyan-400/40 bg-cyan-400/10 text-cyan-200" },
  pago_pendiente_liberacion:              { label: "Pago pendiente liberación por admin",  cls: "border-emerald-400/40 bg-emerald-400/10 text-emerald-300" },
  en_revision:                            { label: "En revisión",                          cls: "border-red-400/40 bg-red-400/10 text-red-300" },
  mediacion_completada:                   { label: "Completada · pago liberado",           cls: "border-emerald-400/40 bg-emerald-400/10 text-emerald-300" },
  cancelada:                              { label: "Cancelada",                            cls: "border-slate-400/40 bg-slate-400/10 text-slate-400" },
};

// Estados donde el mediador debe subir un acta PDF
const MEDIATOR_UPLOAD_STATUS = {
  cierre_sin_acuerdo_pendiente_documento: "acta_frustrada",
  pago_cierre_confirmado: "acta_acuerdo",
};

const CLP = (n) => n ? `$${n.toLocaleString("es-CL")}` : "—";
const fmtDate = (iso) => { try { return new Date(iso).toLocaleString("es-CL", { dateStyle: "short", timeStyle: "short" }); } catch { return iso; } };

export default function MisMediaciones() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [uploading, setUploading] = useState(null); // mediation id

  const isMediator = user?.role === "mediator";
  const isRequester = user?.role === "requester" || user?.role === "lawyer";

  const load = useCallback(async () => {
    setLoading(true); setError("");
    try {
      const { data } = await api.get("/mediation-requests");
      setItems(data.items || []);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { if (user) load(); }, [user, load]);

  const uploadActa = async (mr, kind, file) => {
    if (!file) return;
    if (file.size > 15 * 1024 * 1024) {
      setError("El archivo excede 15 MB.");
      return;
    }
    setUploading(mr.id); setError("");
    try {
      // Convertir a base64
      const b64 = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result.split(",")[1] || "");
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });
      await api.post(`/mediation-requests/${mr.id}/documents`, {
        kind,
        filename: file.name,
        mime: file.type || "application/pdf",
        data_b64: b64,
      });
      await load();
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally { setUploading(null); }
  };

  if (!user) return null;

  return (
    <div className="container-app py-8" data-testid="mis-mediaciones-page">
      <div className="flex items-start gap-3">
        <HeartHandshake size={28} className="text-cyan-300 mt-1"/>
        <div>
          <div className="label-eyebrow text-cyan-300">Panel personal</div>
          <h1 className="serif text-4xl">Mis Mediaciones</h1>
          <p className="text-sm text-muted-foreground mt-2 max-w-2xl">
            {isMediator
              ? "Tus casos activos, estado del pago y acciones pendientes (subida de actas)."
              : "Tus solicitudes de mediación con su estado y monto pagado."}
          </p>
        </div>
      </div>

      {error && (
        <div className="mt-4 p-3 rounded-md border border-red-400/40 bg-red-400/10 text-sm text-red-200 inline-flex items-center gap-2" data-testid="mm-error">
          <AlertTriangle size={14}/> {error}
        </div>
      )}

      {loading ? (
        <div className="panel p-12 mt-6 text-center text-muted-foreground inline-flex items-center gap-2 justify-center w-full">
          <Loader2 size={16} className="animate-spin"/> Cargando…
        </div>
      ) : items.length === 0 ? (
        <div className="panel p-12 mt-6 text-center text-muted-foreground" data-testid="mm-empty">
          <ScrollText size={22} className="mx-auto mb-3"/>
          {isMediator ? "Aún no has recibido solicitudes de mediación." : "Aún no has creado solicitudes de mediación."}
          {isRequester && (
            <div className="mt-4">
              <Link to="/app/mediadores" className="btn-primary inline-flex text-xs py-2" data-testid="mm-cta-mediadores">
                Ver Directorio de Mediadores
              </Link>
            </div>
          )}
        </div>
      ) : (
        <div className="mt-6 space-y-4">
          {items.map((mr) => {
            const st = STATUS_META[mr.status] || { label: mr.status, cls: "border-white/10 bg-white/5" };
            const uploadKind = MEDIATOR_UPLOAD_STATUS[mr.status];
            const showUploadButton = isMediator && uploadKind && mr.mediator_id === user.id;
            const paymentPaid = !!mr.initial_paid_at;
            return (
              <div key={mr.id} className="panel p-5" data-testid={`mm-card-${mr.id}`}>
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div className="flex-1 min-w-[280px]">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className={`pill text-[10px] border ${st.cls}`} data-testid={`mm-status-${mr.id}`}>{st.label}</span>
                      {paymentPaid && <span className="pill text-[10px] border-emerald-400/40 bg-emerald-400/10 text-emerald-300 inline-flex items-center gap-1"><CheckCircle2 size={9}/> Pagado</span>}
                    </div>
                    <h3 className="serif text-xl mt-2 leading-snug">{mr.subject}</h3>
                    <div className="mt-3 grid sm:grid-cols-2 gap-2 text-xs">
                      <div className="inline-flex items-center gap-1.5 text-muted-foreground">
                        <User size={11}/> {isMediator ? "Solicitante" : "Mediador"}:
                        <strong className="text-foreground">{isMediator ? mr.requester_name : mr.mediator_name}</strong>
                      </div>
                      <div className="inline-flex items-center gap-1.5 text-muted-foreground">
                        <Calendar size={11}/> Creada: {fmtDate(mr.created_at)}
                      </div>
                      <div className="inline-flex items-center gap-1.5 text-muted-foreground">
                        <DollarSign size={11}/> Monto solicitante: <strong className="text-foreground">{CLP(mr.total_solicitante_clp || mr.quote_snapshot?.total_solicitante_clp)}</strong>
                      </div>
                      {mr.preferred_modality && (
                        <div className="inline-flex items-center gap-1.5 text-muted-foreground">
                          Modalidad: <strong className="text-foreground">{mr.preferred_modality}</strong>
                        </div>
                      )}
                    </div>
                    {mr.notes && (
                      <div className="mt-3 p-2 rounded-md bg-white/[0.03] border border-white/5 text-xs text-muted-foreground line-clamp-2">
                        {mr.notes}
                      </div>
                    )}
                  </div>
                  <Link
                    to={`/app/mediacion-familiar/${mr.id}`}
                    className="btn-ghost text-xs py-1.5 inline-flex items-center gap-1"
                    data-testid={`mm-open-${mr.id}`}
                  >
                    Ver detalle <ExternalLink size={11}/>
                  </Link>
                </div>

                {/* Subida de acta (solo mediador, en estado correcto) */}
                {showUploadButton && (
                  <div className="mt-4 p-3 rounded-md border border-orange-400/30 bg-orange-400/[0.05]">
                    <div className="flex items-center justify-between gap-3 flex-wrap">
                      <div className="text-xs">
                        <div className="font-medium text-orange-200 inline-flex items-center gap-1">
                          <FileText size={12}/> Acción requerida: subir {uploadKind === "acta_acuerdo" ? "Acta con Acuerdo" : "Acta Frustrada"} (PDF, máx 15 MB)
                        </div>
                        <div className="text-muted-foreground mt-1">Una vez subida, el solicitante deberá aprobarla y el admin autorizará tu pago.</div>
                      </div>
                      <label className="btn-primary text-xs py-1.5 inline-flex items-center gap-1.5 cursor-pointer" data-testid={`mm-upload-${mr.id}`}>
                        {uploading === mr.id ? (
                          <><Loader2 size={11} className="animate-spin"/> Subiendo…</>
                        ) : (
                          <><Upload size={11}/> Subir acta PDF</>
                        )}
                        <input
                          type="file"
                          accept=".pdf,application/pdf"
                          className="hidden"
                          disabled={uploading === mr.id}
                          onChange={(e) => uploadActa(mr, uploadKind, e.target.files?.[0])}
                          data-testid={`mm-upload-input-${mr.id}`}
                        />
                      </label>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
